import React, { forwardRef, ReactNode } from 'react'
import { Box, TextField, TextFieldProps, Typography } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { RADIUS } from '@/shared/styles/radii'

// Notion 流 text input.
// floating label を撤去し、 label を input の上に 13/500 で別 mount.
// input chrome は chip-shape (= 1px border.subtle, radius md, padding 8/12).
//
// label prop は string | ReactNode. helperText / error も pass-through.
// 通常は size="small" / fullWidth な前提 (= Notion 風 form の標準).

interface NotionTextFieldProps
  extends Omit<TextFieldProps, 'variant' | 'label'> {
  label?: ReactNode
}

export const NotionTextField = forwardRef<HTMLDivElement, NotionTextFieldProps>(
  ({ label, fullWidth = true, sx, InputProps, ...rest }, ref) => (
    <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {label && (
        <Typography component="label" sx={PORTAL_TEXT.caption}>
          {label}
        </Typography>
      )}
      <TextField
        variant="outlined"
        size="small"
        fullWidth={fullWidth}
        InputProps={{
          ...InputProps,
          sx: [
            {
              fontSize: 14,
              color: colorTokens.fg.default,
              backgroundColor: colorTokens.bg.default,
              '& fieldset': {
                borderColor: colorTokens.border.subtle,
                transition: 'border-color 120ms ease',
              },
              '&:hover fieldset': {
                borderColor: `${colorTokens.border.default} !important`,
              },
              '&.Mui-focused fieldset': {
                borderColor: `${colorTokens.accent.primary} !important`,
                borderWidth: '1px !important',
              },
              '&.Mui-disabled': {
                backgroundColor: colorTokens.bg.subtle,
                color: colorTokens.fg.disabled,
              },
              '& input': {
                py: 1,
                px: 1.5,
                '&::placeholder': {
                  color: colorTokens.fg.faint,
                  opacity: 1,
                },
              },
            },
            ...(InputProps?.sx
              ? Array.isArray(InputProps.sx)
                ? InputProps.sx
                : [InputProps.sx]
              : []),
          ],
        }}
        sx={[
          {
            '& .MuiOutlinedInput-root': {
              borderRadius: `${RADIUS.md}px`,
            },
            '& .MuiFormHelperText-root': {
              ...PORTAL_TEXT.cellMeta,
              mx: 0,
              mt: 0.5,
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...rest}
      />
    </Box>
  )
)
NotionTextField.displayName = 'NotionTextField'
